import { memo } from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import AddModeratorIcon from "@mui/icons-material/AddModerator";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SportsMartialArtsIcon from "@mui/icons-material/SportsMartialArts";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

// -----------------------------------------------------------------------------

const RolesBase: { [prop: string]: RoleDefinition } = {
  UnprivilegedUser: {
    label: "Member",
    role: "UnprivilegedUser",
    icon: <VerifiedUserIcon />,
    definition:
      "Can view and edit all data in the organization. Cannot invite other members. Cannot change organization settings. Cannot delete the organization. Cannot change the organization's subscription. Cannot change the organization's billing information. Cannot change the organization's payment method.",
  },
  Auditor: {
    label: "Auditor",
    role: "Auditor",
    icon: <AddModeratorIcon />,
    definition:
      "Can view all data in the organization. Cannot edit data. Cannot invite new members. Cannot edit member roles. Cannot edit organization settings. Cannot edit organization billing. Cannot edit organization members.",
  },
  Admin: {
    label: "Admin",
    role: "Admin",
    icon: <AdminPanelSettingsIcon />,
    definition:
      "Can view and edit all data in the organization. Can invite new members. Can edit member roles. Can edit organization settings. Can edit organization billing. Can edit organization members.",
  },
  // Billing: {
  //   label: "Billing",
  //   role: "Billing",
  //   icon: <AttachMoneyIcon />,
  //   definition: "foo bar baz",
  // },
};

const SuperAdminRoles: { [prop: string]: RoleDefinition } = {
  ...RolesBase,
  SuperAdmin: {
    label: "Super Admin",
    role: "SuperAdmin",
    icon: <SportsMartialArtsIcon />,
    definition:
      "Can do anything. Can invite new members. Can change the organization name. Can change the organization plan. Can change the organization billing information. Can change the organization members. Can change the organization settings. Can change the organization integrations. Can change the organization API keys.",
  },
};

export { RolesBase, SuperAdminRoles };

// -----------------------------------------------------------------------------

const OrganizationMembersRoleDefinitions = memo(
  function OrganizationMembersRoleDefinitions(): JSX.Element {
    const roles = Object.values(SuperAdminRoles);

    return (
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            padding: 2,
          }}
        >
          <Typography
            sx={{
              ml: 2,
              mt: 2,
            }}
            variant="h5"
          >
            Roles
          </Typography>
          <Typography
            sx={{
              ml: 2,
            }}
            variant="subtitle1"
            gutterBottom
          >
            Roles define what a member can do in the organization.
          </Typography>
          <List>
            {roles.map((role) => (
              <ListItem alignItems="flex-start" key={role.role}>
                <ListItemIcon>{role.icon}</ListItemIcon>
                <ListItemText
                  primary={role.label}
                  secondary={role.definition}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>
    );
  }
);

export default OrganizationMembersRoleDefinitions;

// -----------------------------------------------------------------------------

interface RoleDefinition {
  label: string;
  role: string;
  icon: JSX.Element;
  definition: string;
}
